import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import clsx from 'clsx';
import * as React from 'react';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';

import { Label } from './label';

function RadioGroup({
  className,
  ...props
}: React.ComponentProps<typeof RadioGroupPrimitive.Root>) {
  return (
    <RadioGroupPrimitive.Root
      data-slot="radio-group"
      className={className}
      {...props}
    />
  );
}

function RadioGroupItem({
  className,
  ...props
}: React.ComponentProps<typeof RadioGroupPrimitive.Item>) {
  return (
    <RadioGroupPrimitive.Item
      data-slot="radio-group-item"
      className={clsx(
        'text-primary focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 size-4 shadow-xs data-[state=checked]:border-fx-primary data-[state=checked]:bg-fx-primary border-fx-neutral-400 aspect-square shrink-0 rounded-full border outline-none transition-[color,box-shadow] focus-visible:ring disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...props}
    >
      <RadioGroupPrimitive.Indicator
        data-slot="radio-group-indicator"
        className="relative flex items-center justify-center"
      >
        <div className="h-1.5 w-1.5 rounded-full bg-white" />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  );
}

interface RadioOption {
  value: string;
  label: React.ReactNode;
  id: string;
}

interface FormRadioGroupProps<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  options: RadioOption[];
  className?: string;
  radioGroupClassName?: string;
  onChange?: (value: string) => void;
}

function FormRadioGroup<T extends FieldValues>({
  control,
  name,
  options,
  className,
  radioGroupClassName,
  onChange,
}: FormRadioGroupProps<T>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <RadioGroup
          onValueChange={value => {
            field.onChange(value);
            onChange?.(value);
          }}
          value={field.value}
          className={clsx('flex gap-6', radioGroupClassName)}
        >
          {options.map(option => (
            <div
              key={option.id}
              className={clsx('flex w-fit items-center space-x-2', className)}
            >
              <RadioGroupItem value={option.value} id={option.id} />
              <Label htmlFor={option.id}>{option.label}</Label>
            </div>
          ))}
        </RadioGroup>
      )}
    />
  );
}

export { FormRadioGroup, RadioGroup, RadioGroupItem };
